import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

const StatusPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${
      ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
    }`}
  >
    {ok ? <CheckCircle size={13} weight="fill" /> : <XCircle size={13} weight="fill" />}
    {label}
  </span>
);

const SummaryCard = ({ label, value, accent, testId }) => (
  <div className="border border-border bg-card p-4" data-testid={testId}>
    <div className="label-caps text-muted-foreground">{label}</div>
    <div className={`mt-1 text-2xl font-black font-heading ${accent || ""}`}>{value}</div>
  </div>
);

export default function MetaEvents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/meta-events?limit=200")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cfg = data?.config || {};
  const s = data?.summary || {};
  const events = data?.events || [];

  return (
    <div data-testid="meta-events-page">
      <PageHeader title="Meta Event Debug" subtitle="Pixel + Conversions API (CAPI) event delivery log." />

      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={load} data-testid="meta-refresh-btn">
          <ArrowsClockwise size={15} /> Refresh
        </Button>
        {!cfg.capi_configured && (
          <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 font-semibold" data-testid="meta-capi-warning">
            <Warning size={16} weight="fill" /> CAPI access token not set — server-side events are disabled (browser Pixel still fires).
          </span>
        )}
        {cfg.test_mode && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-sm bg-blue-100 text-blue-700" data-testid="meta-test-mode">
            TEST MODE active
          </span>
        )}
      </div>

      <div className="border border-border bg-secondary/40 p-4 mb-6 text-sm" data-testid="meta-config">
        <span className="font-semibold">Pixel ID:</span> <span className="mono">{cfg.pixel_id || "—"}</span>
        <span className="mx-3 text-muted-foreground">|</span>
        <span className="font-semibold">Graph API:</span> <span className="mono">{cfg.graph_version || "—"}</span>
        <span className="mx-3 text-muted-foreground">|</span>
        <span className="font-semibold">CAPI:</span>{" "}
        <span className={cfg.capi_configured ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>
          {cfg.capi_configured ? "Configured" : "Not configured"}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Events (30d)" value={s.total_30d ?? 0} testId="meta-summary-total" />
        <SummaryCard label="CAPI Success" value={s.success_30d ?? 0} accent="text-green-700" testId="meta-summary-success" />
        <SummaryCard label="CAPI Failed" value={s.failed_30d ?? 0} accent="text-red-700" testId="meta-summary-failed" />
        <SummaryCard label="Not Configured" value={s.not_configured_30d ?? 0} accent="text-amber-700" testId="meta-summary-notcfg" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="meta-events-table">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left label-caps text-muted-foreground">
              <th className="p-3">Event Name</th>
              <th className="p-3">Timestamp</th>
              <th className="p-3">Source</th>
              <th className="p-3">Pixel</th>
              <th className="p-3">CAPI</th>
              <th className="p-3">Result</th>
              <th className="p-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading events…</td></tr>
            )}
            {!loading && events.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="meta-events-empty">No events recorded yet. Trigger an action (e.g. submit a lead or sign up) to see events here.</td></tr>
            )}
            {events.map((e, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`meta-event-row-${i}`}>
                <td className="p-3 font-semibold">{e.event_name}</td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                <td className="p-3 text-muted-foreground">{e.source}</td>
                <td className="p-3">{e.pixel_status === "fired" ? <StatusPill ok label="Fired" /> : <span className="text-xs text-muted-foreground">n/a</span>}</td>
                <td className="p-3"><span className="text-xs font-semibold">{e.capi_status}</span></td>
                <td className="p-3">
                  {e.capi_status === "not_configured"
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm bg-amber-100 text-amber-700"><Warning size={13} weight="fill" />CAPI off</span>
                    : <StatusPill ok={e.success} label={e.success ? "Success" : "Failed"} />}
                </td>
                <td className="p-3 text-xs text-muted-foreground max-w-[260px] truncate" title={e.error || ""}>
                  {e.error || (e.custom_keys?.length ? `data: ${e.custom_keys.join(", ")}` : "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
